import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TablePagination,
  Box,
  Typography,
  Chip,
  Checkbox,
  alpha,
  Fade,
} from '@mui/material';
import { Edit, Delete, Visibility, MoreVert } from '@mui/icons-material';
import EmptyState from './EmptyState';
import { TableSkeleton } from './LoadingState';

const DataTable = ({
  columns,
  data,
  onEdit,
  onDelete,
  onView,
  onRowClick,
  loading = false,
  emptyMessage = 'No hay datos disponibles',
  emptyDescription,
  emptyIcon,
  emptyAction,
  emptyActionLabel,
  page = 0,
  rowsPerPage = 10,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
  selectable = false,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  hoverable = true,
  stickyHeader = false,
  maxHeight,
  compact = false,
  sx = {},
}) => {
  const renderCellValue = (column, row) => {
    const value = column.accessor ? row[column.accessor] : null;

    if (column.render) {
      return column.render(value, row);
    }

    if (typeof value === 'boolean') {
      return (
        <Chip
          label={value ? 'Sí' : 'No'}
          size="small"
          sx={{
            fontWeight: 600,
            backgroundColor: value
              ? (theme) => alpha(theme.palette.success.main, 0.12)
              : (theme) => alpha(theme.palette.grey[500], 0.12),
            color: value ? 'success.dark' : 'text.secondary',
          }}
        />
      );
    }

    if (value === null || value === undefined) {
      return (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      );
    }

    return value;
  };

  const isSelected = (rowId) => selectedRows.includes(rowId);
  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected = selectedRows.length > 0 && selectedRows.length < data.length;

  if (loading) {
    return (
      <Paper sx={{ overflow: 'hidden', ...sx }}>
        <TableSkeleton rows={rowsPerPage} columns={columns.length + (onEdit || onDelete || onView ? 1 : 0)} />
      </Paper>
    );
  }

  if (data.length === 0) {
    return (
      <Paper sx={{ overflow: 'hidden', ...sx }}>
        <EmptyState
          icon={emptyIcon}
          title={emptyMessage}
          description={emptyDescription}
          actionLabel={emptyActionLabel}
          onAction={emptyAction}
          size="medium"
          sx={{ py: 6 }}
        />
      </Paper>
    );
  }

  return (
    <Paper sx={{ overflow: 'hidden', ...sx }}>
      <TableContainer sx={{ maxHeight: maxHeight }}>
        <Table stickyHeader={stickyHeader} size={compact ? 'small' : 'medium'}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={(e) => onSelectAll?.(e.target.checked)}
                    sx={{
                      color: 'text.secondary',
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                        color: 'primary.main',
                      },
                    }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.accessor || column.header}
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    minWidth: column.minWidth,
                    width: column.width,
                  }}
                >
                  {column.header}
                </TableCell>
              ))}
              {(onEdit || onDelete || onView) && (
                <TableCell
                  sx={{
                    fontWeight: 600,
                    width: 120,
                    textAlign: 'right',
                  }}
                >
                  Acciones
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => {
              const rowId = row.id || index;
              const selected = isSelected(rowId);

              return (
                <Fade in key={rowId} timeout={300} style={{ transitionDelay: `${index * 30}ms` }}>
                  <TableRow
                    hover={hoverable}
                    selected={selected}
                    onClick={() => onRowClick?.(row)}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&.Mui-selected': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
                        },
                      },
                    }}
                  >
                    {selectable && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selected}
                          onChange={(e) => {
                            e.stopPropagation();
                            onSelectRow?.(rowId, e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            color: 'text.secondary',
                            '&.Mui-checked': {
                              color: 'primary.main',
                            },
                          }}
                        />
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell
                        key={column.accessor || column.header}
                        sx={{
                          maxWidth: column.maxWidth,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: column.wrap ? 'normal' : 'nowrap',
                        }}
                      >
                        {renderCellValue(column, row)}
                      </TableCell>
                    ))}
                    {(onEdit || onDelete || onView) && (
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 0.5,
                            justifyContent: 'flex-end',
                          }}
                        >
                          {onView && (
                            <Tooltip title="Ver detalles" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onView(row);
                                }}
                                sx={{
                                  color: 'info.main',
                                  '&:hover': {
                                    backgroundColor: (theme) =>
                                      alpha(theme.palette.info.main, 0.08),
                                  },
                                }}
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onEdit && (
                            <Tooltip title="Editar" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(row);
                                }}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: (theme) =>
                                      alpha(theme.palette.primary.main, 0.08),
                                  },
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {onDelete && (
                            <Tooltip title="Eliminar" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDelete(row);
                                }}
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    backgroundColor: (theme) =>
                                      alpha(theme.palette.error.main, 0.08),
                                  },
                                }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                </Fade>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {totalCount !== undefined && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => onPageChange?.(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) =>
            onRowsPerPageChange?.(parseInt(e.target.value, 10))
          }
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          sx={{
            borderTop: '1px solid',
            borderColor: 'divider',
            '& .MuiTablePagination-toolbar': {
              minHeight: 56,
            },
          }}
        />
      )}
    </Paper>
  );
};

export default DataTable;
